import { IElementWaypoint } from "@/hooks/use-placeholders";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { useAppSelector } from "@/store/store-hooks";
import { selectIElementWorldPosition } from "@faro-lotv/app-component-toolbox";
import { GUID } from "@faro-lotv/foundation";
import {
  IElement,
  IElementGenericAnnotation,
  isIElementAreaSection,
  isIElementExternalMarkup,
  isIElementGenericAnnotation,
  isIElementGenericDataset,
  isIElementImg2d,
  isIElementImg360,
  isIElementImageCommand,
  isIElementMarkup,
  isIElementMarkupCommand,
  isIElementPdfCommand,
  isIElementSection,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  selectAncestor,
  selectChildrenDepthFirst,
  selectIElementChildren,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { minBy } from "es-toolkit";
import { Vector3 } from "three";

/** Annotations taken inside each capture, keyed by the capture (waypoint) id */
export type CaptureAnnotations = Map<GUID, IElementGenericAnnotation[]>;

/**
 * Assign each annotation to the capture it was taken in, skipping the action objects
 * that are not relevant for a capture (info text and links):
 * - the capture itself when the annotation is its descendant (e.g. image points on a 360)
 * - otherwise the closest capture inside the annotation's own section (e.g. the 360 section).
 * Annotations stored in a dataset or an area (e.g. taken on a point cloud or a sheet) belong to no capture.
 *
 * @param iElements The iElements state slice
 * @param waypoints The capture waypoints shown in the view
 * @param annotations The annotations available in the view
 * @returns the annotations taken inside each capture
 */
function computeCaptureAnnotations(
  iElements: IElementsState,
  waypoints: IElementWaypoint[],
  annotations: IElementGenericAnnotation[],
): CaptureAnnotations {
  const state = { iElements };
  const waypointIds = new Set(waypoints.map((w) => w.id));
  const result: CaptureAnnotations = new Map();

  for (const annotation of annotations) {
    if (!isCaptureRelevantAnnotation(annotation, state)) continue;
    const owner =
      selectAncestor(annotation, (el) => waypointIds.has(el.id))(state) ??
      closestCaptureInSection(annotation, waypoints, state);
    if (!owner) continue;
    result.set(owner.id, [...(result.get(owner.id) ?? []), annotation]);
  }
  return result;
}

/**
 * @param annotation The annotation to check
 * @param state The iElements state
 * @returns true for markups, external issues, and image or PDF action objects
 */
function isCaptureRelevantAnnotation(
  annotation: IElementGenericAnnotation,
  state: OnlyIElementsStore,
): boolean {
  return selectIElementChildren(annotation.id)(state).some(
    (child) =>
      (isIElementMarkup(child) && !isIElementMarkupCommand(child)) ||
      isIElementExternalMarkup(child) ||
      isIElementImageCommand(child) ||
      isIElementPdfCommand(child),
  );
}

function closestCaptureInSection(
  annotation: IElementGenericAnnotation,
  waypoints: IElementWaypoint[],
  state: OnlyIElementsStore,
): IElement | undefined {
  const section = selectAncestor(annotation, isIElementSection)(state);
  if (
    !section ||
    isIElementGenericDataset(section) ||
    isIElementAreaSection(section)
  ) {
    return;
  }

  const candidates = waypoints.filter(
    (w) => !!selectAncestor(w, (el) => el.id === section.id)(state),
  );
  if (candidates.length <= 1) return candidates.at(0);

  const position = new Vector3().fromArray(
    selectIElementWorldPosition(annotation.id)(state),
  );
  const distances = new Map(
    candidates.map((w) => [
      w,
      position.distanceToSquared(
        new Vector3().fromArray(selectIElementWorldPosition(w.id)(state)),
      ),
    ]),
  );
  return minBy(candidates, (w) => distances.get(w) ?? Infinity);
}

const NO_ANNOTATIONS: IElementGenericAnnotation[] = [];

/**
 * Only the captures and annotations of the capture's own section are checked, so it is cheap
 * enough to run for the hovered capture
 *
 * @param state The app state
 * @param capture The capture to list the annotations of
 * @returns the annotations taken inside the capture
 */
export const selectCaptureAnnotationList = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, capture: IElementWaypoint | undefined) =>
        capture,
    ],
    (iElements, capture): IElementGenericAnnotation[] => {
      if (!capture) return NO_ANNOTATIONS;
      const state = { iElements };
      const section = selectAncestor(capture, isIElementSection)(state);
      // Annotations stored in a dataset or an area belong to no capture
      const scope =
        !section ||
        isIElementGenericDataset(section) ||
        isIElementAreaSection(section)
          ? capture
          : section;
      const annotations = selectChildrenDepthFirst(
        scope,
        isIElementGenericAnnotation,
      )(state);
      const captures = selectChildrenDepthFirst(
        scope,
        (el): el is IElementWaypoint =>
          isIElementImg360(el) || isIElementImg2d(el),
      )(state);
      return (
        computeCaptureAnnotations(iElements, captures, annotations).get(
          capture.id,
        ) ?? NO_ANNOTATIONS
      );
    },
  ),
);

/**
 * @param capture The capture to list the annotations of
 * @returns the annotations taken inside the capture
 */
export function useCaptureAnnotationList(
  capture: IElementWaypoint | undefined,
): IElementGenericAnnotation[] {
  return useAppSelector(selectCaptureAnnotationList(capture));
}
